import type {ReactElement} from 'react';
import React, {useMemo} from 'react';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import type {BirthdayFormat} from '@FormMakerReact/form-maker.types';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2/toggle-switch';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text/text';
import {ClickableDiv} from '@Components/clickable-div';
import {FieldEditRow} from '@FormMakerReact/layout/form-maker-right-panel/controls/field-edit-row/field-edit-row';
import type {FieldEditRowKind} from '@FormMakerReact/layout/form-maker-right-panel/controls/field-edit-row/field-edit-row.types';
import {FieldDragHandle} from '../field-drag-handle/field-drag-handle';
import type {FieldDragHandleProps} from '../field-drag-handle/field-drag-handle.types';
import type {FieldToggleRowProps} from './field-toggle-row.types';
import styles from './field-toggle-row.module.scss';

export function FieldToggleRow({
  boxType,
  label,
  iconClass,
  isEnabled,
  isLocked,
  isExpandable,
  isEditOpen,
  editKind,
  editLabelValue,
  editBirthdayFormat,
  isRequired,
  onToggle,
  onEditToggle,
  onLabelChange,
  onFormatChange,
  onRequiredChange,
  onMoveUp,
  onMoveDown,
  isFirstMovable,
  isLastMovable,
}: FieldToggleRowProps): ReactElement {
  const sortable = useSortable({id: boxType, disabled: isLocked});
  const {isDragging, transform, transition, attributes, listeners, setActivatorNodeRef, setNodeRef} = sortable;

  const style = useMemo<React.CSSProperties>(
    () => ({
      opacity: isDragging ? 0.4 : undefined,
      transform: CSS.Translate.toString(transform),
      transition,
      zIndex: isDragging ? 1 : undefined,
    }),
    [isDragging, transform, transition]
  );

  const dragHandleProps: FieldDragHandleProps = {
    attributes,
    listeners,
    setActivatorNodeRef,
    label,
    onMoveUp,
    onMoveDown,
    isFirstMovable,
    isLastMovable,
  };

  return (
    <div ref={setNodeRef} style={style} className={resolveRowClassName(isEditOpen, isDragging, isLocked)} {...(listeners ?? {})}>
      <div className={styles.rowHeader}>
        <FieldDragHandle {...dragHandleProps} />
        {renderClickableHeader(isExpandable, isEditOpen, label, iconClass, onEditToggle)}
        <div className={styles.toggleArea}>{renderToggleSwitch(isEnabled, isLocked, onToggle, label)}</div>
      </div>
      {renderEditPanel(boxType, isExpandable, isEditOpen, editKind, editLabelValue, editBirthdayFormat, isRequired, onLabelChange, onFormatChange, onRequiredChange)}
    </div>
  );
}

function resolveRowClassName(isEditOpen: boolean, isDragging: boolean, isLocked: boolean): string {
  return [styles.row, isLocked ? styles.rowDisabled : '', isEditOpen ? styles.rowOpen : '', isDragging ? styles.rowDragging : ''].filter(Boolean).join(' ');
}

function renderClickableHeader(isExpandable: boolean, isEditOpen: boolean, label: string, iconClass: string, onEditToggle: () => void): ReactElement {
  const labelAndIcon = (
    <div className={styles.labelGroup}>
      <Icon icon={iconClass} size={IconSize.SIZE_ICON_16} className={styles.fieldIcon} />
      <Text val={label} size={TextSize.MD} className={isEditOpen ? styles.labelOpen : ''} />
    </div>
  );

  if (!isExpandable) {
    return <div className={styles.headerStatic}>{labelAndIcon}</div>;
  }

  return (
    <ClickableDiv className={styles.headerClickable} onClick={onEditToggle} aria-expanded={isEditOpen} aria-label={window.i18next.t('pmwjs_edit_field')}>
      {labelAndIcon}
      {renderCaretIcon(isEditOpen)}
    </ClickableDiv>
  );
}

function renderCaretIcon(isEditOpen: boolean): ReactElement {
  const caretIcon = isEditOpen ? 'icon-caret-up' : 'icon-caret-down';
  return <Icon icon={caretIcon} size={IconSize.SIZE_ICON_16} className={styles.caretIcon} />;
}

function renderToggleSwitch(isEnabled: boolean, isLocked: boolean, onToggle: () => void, fieldLabel: string): ReactElement {
  const inputId = `field-toggle-${fieldLabel.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <ToggleSwitch
      id={inputId}
      isChecked={isEnabled}
      isDisabled={isLocked}
      onChange={onToggle}
      elementTitle={`${window.i18next.t('pmwjs_toggle_field')} ${fieldLabel}`}
      size={ToggleSwitchV2Size.LARGE}
    />
  );
}

function renderEditPanel(
  rowId: string,
  isExpandable: boolean,
  isEditOpen: boolean,
  editKind: FieldEditRowKind,
  editLabelValue: string,
  editBirthdayFormat: BirthdayFormat,
  isRequired: boolean,
  onLabelChange: (v: string) => void,
  onFormatChange: (f: BirthdayFormat) => void,
  onRequiredChange: (isRequired: boolean) => void
): ReactElement | null {
  if (!isExpandable) {
    return null;
  }

  return (
    <FieldEditRow
      rowId={rowId}
      kind={editKind}
      labelValue={editLabelValue}
      birthdayFormat={editBirthdayFormat}
      isRequired={isRequired}
      isOpen={isEditOpen}
      onLabelChange={onLabelChange}
      onFormatChange={onFormatChange}
      onRequiredChange={onRequiredChange}
    />
  );
}
